<script setup lang="ts">
import { useCartStore } from '@/stores/cart'
import { logApiError } from '~/utils/logger'
import { containsSuspiciousScript } from '~/utils/sanitize'

interface Props {
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

const emit = defineEmits<{
  pay: []
}>()

const cartStore = useCartStore()
const { createBooking } = useBookings()
const { redirectToCheckout } = useStripe()

// Loading and error states
const isSubmitting = ref(false)
const submitError = ref<string | null>(null)

// Coupon state
const couponInput = ref('')
const couponError = ref<string | null>(null)
const applyingCoupon = ref(false)

// Countries for invoice
const countries = [
  { code: 'BE', name: 'België' },
  { code: 'NL', name: 'Nederland' },
  { code: 'DE', name: 'Duitsland' },
  { code: 'FR', name: 'Frankrijk' },
  { code: 'LU', name: 'Luxemburg' },
  { code: 'GB', name: 'Verenigd Koninkrijk' },
  { code: 'ES', name: 'Spanje' },
  { code: 'IT', name: 'Italië' },
  { code: 'PT', name: 'Portugal' },
  { code: 'AT', name: 'Oostenrijk' },
  { code: 'CH', name: 'Zwitserland' },
  { code: 'PL', name: 'Polen' },
  { code: 'CZ', name: 'Tsjechië' },
  { code: 'DK', name: 'Denemarken' },
  { code: 'SE', name: 'Zweden' },
  { code: 'NO', name: 'Noorwegen' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ierland' },
]

// Computed values
const getTotalPrice = computed(() => Number(cartStore.total ?? 0))
const discountDetails = computed(() => cartStore.discount_details ?? [])

// Count unique participants based on rijksregisternummer
const uniqueParticipantCount = computed(() => {
  const uniqueIds = new Set<string>()

  cartStore.participants.forEach(p => {
    if (p.rijksregisternummer) {
      // Normalize by removing non-digits
      const normalized = p.rijksregisternummer.replace(/\D/g, '')
      if (normalized)
        uniqueIds.add(normalized)
    }
  })

  // Return unique count, or fallback to participants.length if no IDs are set yet
  return uniqueIds.size || cartStore.participants.length
})

// Check if invoice is enabled for this camp
const invoiceEnabled = computed(() => cartStore.camp_enable_invoice ?? false)

// Get available payment methods from camp settings
const paymentMethods = computed<Array<{ value: string, title: string, desc: string, color: string }>>(() => {
  const campMethods = cartStore.camp_payment_methods

  if (!campMethods || campMethods.length === 0) {
    return [
      {
        value: 'stripe',
        title: 'Online Betaling',
        desc: 'Bancontact en Kredietkaart',
        color: 'primary',
      },
    ]
  }

  const methodMap: Record<string, { value: string, title: string, desc: string, color: string }> = {
    bancontact: {
      value: 'stripe',
      title: 'Bancontact',
      desc: 'Betaal met Bancontact',
      color: 'primary',
    },
    creditcard: {
      value: 'stripe',
      title: 'Kredietkaart',
      desc: 'Betaal met kredietkaart',
      color: 'primary',
    },
  }

  const availableMethods = campMethods
    .map(method => methodMap[method])
    .filter((m): m is { value: string, title: string, desc: string, color: string } => m !== undefined)

  if (availableMethods.length > 0 && availableMethods.every(m => m.value === 'stripe')) {
    const methods = campMethods.filter(m => methodMap[m] !== undefined)

    return [{
      value: 'stripe',
      title: 'Online Betaling',
      desc: methods.map(m => methodMap[m]?.title).filter(Boolean).join(' en '),
      color: 'primary',
    }]
  }

  return availableMethods
})

const selectedPaymentMethod = ref('stripe')

// Refund policy info
const refundPolicyInfo = computed(() => {
  const policy = cartStore.camp_refund_policy || 'admin_fee_15'

  const policyMap: Record<string, { title: string, description: string, icon: string, color: string }> = {
    admin_fee_15: {
      title: 'Administratieve kost',
      description: 'Bij annulatie van één of meerdere tickets betalen we het bedrag terug, met een vaste administratieve kost van €15.',
      icon: 'bx-info-circle',
      color: 'info',
    },
    flexible: {
      title: 'Flexibel',
      description: 'Volledige terugbetaling tot 7 dagen voor het evenement',
      icon: 'bx-check-circle',
      color: 'success',
    },
    moderate: {
      title: 'Matig',
      description: '50% terugbetaling tot 14 dagen voor het evenement',
      icon: 'bx-info-circle',
      color: 'warning',
    },
    strict: {
      title: 'Strikt',
      description: 'Geen terugbetalingen mogelijk',
      icon: 'bx-x-circle',
      color: 'error',
    },
  }

  return policyMap[policy] || policyMap.admin_fee_15
})

// Check if forms are complete
const formsComplete = computed(() => cartStore.allFormsComplete)
const hasCampForms = computed(() => cartStore.hasCampForms)
const missingFormFields = computed(() => cartStore.missingFormFieldsPerParticipant)

// Check if can proceed to payment
const canSubmit = computed(() => {
  return cartStore.canProceedToPayment && formsComplete.value && !props.disabled
})

// Get list of missing requirements
const missingRequirements = computed(() => {
  const missing: string[] = []

  // Check if cart is empty
  if (cartStore.items.length === 0) {
    missing.push('Voeg tickets toe aan je winkelmandje')
  }

  // Check if all participants have basic info
  const participantsWithoutBasicInfo = cartStore.participants.filter(
    p => !p.first_name || !p.last_name || (!p.no_rrn && (!p.rijksregisternummer || !p.date_of_birth)),
  )

  if (participantsWithoutBasicInfo.length > 0) {
    // Get specific names or ticket numbers
    const names = participantsWithoutBasicInfo.map(p => {
      if (p.first_name && p.last_name)
        return `${p.first_name} ${p.last_name}`

      const ticketIndex = cartStore.participants.indexOf(p)

      return `Ticket ${ticketIndex + 1}`
    })

    if (names.length === 1) {
      missing.push(`Vul de basisgegevens in voor ${names[0]}`)
    }
    else if (names.length <= 3) {
      missing.push(`Vul de basisgegevens in voor: ${names.join(', ')}`)
    }
    else {
      missing.push(`Vul de basisgegevens in voor ${names.length} deelnemers`)
    }
  }

  // Check specifically for missing date_of_birth (from rijksregisternummer)
  const participantsWithoutDOB = cartStore.participants.filter(
    p => p.rijksregisternummer && !p.date_of_birth,
  )

  if (participantsWithoutDOB.length > 0) {
    missing.push('Controleer de rijksregisternummers - geboortedatum kon niet worden afgeleid')
  }

  // Check if forms are complete - with detailed info per participant
  if (hasCampForms.value && !formsComplete.value && missingFormFields.value.length > 0) {
    missingFormFields.value.forEach(({ participantName, missingFields }) => {
      if (missingFields.length === 1) {
        missing.push(`${participantName}: vul "${missingFields[0]}" in`)
      }
      else if (missingFields.length <= 3) {
        missing.push(`${participantName}: vul in: ${missingFields.join(', ')}`)
      }
      else {
        missing.push(`${participantName}: ${missingFields.length} verplichte velden niet ingevuld`)
      }
    })
  }

  // Check invoice requirements if enabled
  if (cartStore.invoice_required) {
    if (!cartStore.invoice_company_name) {
      missing.push('Vul de bedrijfsnaam in voor de factuur')
    }
    if (!cartStore.invoice_address) {
      missing.push('Vul het factuuradres in')
    }
    if (!cartStore.invoice_country) {
      missing.push('Selecteer een land voor de factuur')
    }
  }

  return missing
})

// Format discount detail label
const formatDiscountDetailLabel = (detail: { type: 'rule' | 'coupon', name?: string }) => {
  const labelPrefix = detail.type === 'coupon' ? 'Coupon' : 'Kortingsregel'

  return detail.name ? `${labelPrefix}: ${detail.name}` : labelPrefix
}

// Apply coupon
const applyCoupon = async () => {
  if (!couponInput.value.trim())
    return

  applyingCoupon.value = true
  couponError.value = null

  try {
    const result = await cartStore.applyCoupon(couponInput.value.trim())
    if (!result.success)
      couponError.value = result.message || 'Ongeldige kortingscode'
  }
  catch (err) {
    const rawMessage = (err as Error)?.message || ''

    const isInvalidCoupon = rawMessage.includes('Ongeldige kortingscode')
      || rawMessage.includes('validate-coupon')
      || rawMessage.includes('/api/')
      || rawMessage.includes('404')

    couponError.value = isInvalidCoupon
      ? 'Ongeldige kortingscode'
      : 'Fout bij toepassen kortingscode'
  }
  finally {
    applyingCoupon.value = false
  }
}

// Remove coupon
const removeCoupon = () => {
  cartStore.removeCoupon()
  couponInput.value = ''
  couponError.value = null
}

// Complete payment
const completePayment = async () => {
  if (!formsComplete.value) {
    submitError.value = 'Niet alle verplichte formuliervelden zijn ingevuld.'

    return
  }

  // Reject obvious script-injection patterns (e.g. <script>, javascript:, onerror=).
  // Plain '<' and '>' (e.g. "2 > 1", "<3", "-->") remain allowed.
  const suspiciousFields: string[] = []

  const checkField = (label: string, value: unknown) => {
    if (typeof value === 'string' && containsSuspiciousScript(value))
      suspiciousFields.push(label)
  }

  cartStore.participants.forEach((p, index) => {
    const who = `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Deelnemer ${index + 1}`

    checkField(`${who}: medische info`, p.medical_info)
    checkField(`${who}: allergieën`, p.allergies)
    checkField(`${who}: noodcontact`, p.emergency_contact_name)

    p.form_responses?.forEach(r => {
      const answer = r.answer
      if (typeof answer === 'string')
        checkField(`${who}: formulierantwoord`, answer)
      else if (Array.isArray(answer))
        answer.forEach(a => checkField(`${who}: formulierantwoord`, a))
    })
  })

  checkField('Opmerkingen', cartStore.notes)
  checkField('Bedrijfsnaam (factuur)', cartStore.invoice_company_name)
  checkField('Adres (factuur)', cartStore.invoice_address)

  if (suspiciousFields.length > 0) {
    submitError.value
      = `Sommige velden bevatten niet-toegestane tekens (zoals <script>, javascript: of HTML event handlers). `
        + `Pas deze velden aan en probeer opnieuw: ${
          Array.from(new Set(suspiciousFields)).join(', ')}`

    return
  }

  isSubmitting.value = true
  submitError.value = null

  try {
    const availabilityCheck = await cartStore.validateAvailability()
    if (!availabilityCheck.ok) {
      submitError.value = availabilityCheck.message || 'Er zijn niet genoeg tickets beschikbaar.'
      isSubmitting.value = false

      return
    }

    const payload = cartStore.prepareBookingPayload()
    const response = await createBooking(payload)

    if (response.success && response.data) {
      // Check if booking was auto-confirmed (€0 / free booking)
      const bookingData = response.data as Record<string, unknown>
      if (bookingData.auto_confirmed) {
        cartStore.clearCart()
        navigateTo(`/checkout/success?booking_id=${bookingData.id}`)

        return
      }

      if (selectedPaymentMethod.value === 'stripe') {
        // Redirect to Stripe Checkout page
        await redirectToCheckout(response.data.id)

        // Note: redirectToCheckout will navigate away, so code below won't execute
      }
      else if (selectedPaymentMethod.value === 'overschrijving') {
        cartStore.clearCart()
        emit('pay')
      }
    }
    else {
      throw new Error(response.message || 'Boeking maken mislukt')
    }
  }
  catch (error) {
    logApiError('/api/bookings', error, { operation: 'createBooking' })
    submitError.value = (error as Error).message || 'Er is een fout opgetreden bij het maken van de boeking. Probeer het opnieuw.'
    isSubmitting.value = false
  }
}

// Expose methods for parent component
defineExpose({
  completePayment,
  isSubmitting,
  canPay: canSubmit,
  isPaymentActive: computed(() => false), // No embedded payment element anymore
})
</script>

<template>
  <div class="cart2-payment-summary">
    <!-- Cart Summary View -->
    <VCard flat color="rgba(var(--v-theme-on-surface), var(--v-hover-opacity))">
      <VCardText>
        <!-- Camp Name -->
        <div v-if="cartStore.camp_name" class="mb-4">
          <h5 class="text-h5 font-weight-bold">
            {{ cartStore.camp_name }}
          </h5>
        </div>

        <!-- Ticket Items from Cart -->
        <div class="my-5">
          <div v-for="item in cartStore.items" :key="item.ticket_type_id" class="d-flex justify-space-between">
            <span class="text-body-1">{{ item.ticket_type_name }} ({{ item.quantity }}x)</span>
            <h6 class="text-h6">
              €{{ (item.price * item.quantity).toFixed(2) }}
            </h6>
          </div>
        </div>

        <VDivider class="my-4" />

        <!-- Discount Details -->
        <template v-if="discountDetails.length > 0">
          <div
            v-for="(detail, index) in discountDetails"
            :key="index"
            class="d-flex justify-space-between mb-2"
          >
            <div class="d-flex align-center gap-1">
              <VIcon icon="bx-purchase-tag" size="16" color="success" />
              <span class="text-success text-body-2">{{ formatDiscountDetailLabel(detail) }}</span>
            </div>
            <span class="text-success">-€{{ detail.amount?.toFixed(2) || '0.00' }}</span>
          </div>
        </template>

        <!-- Coupon Section -->
        <div class="my-4">
          <div
            v-if="cartStore.coupon_code"
            class="d-flex align-center justify-space-between pa-3 rounded-lg bg-success-subtle"
          >
            <div class="d-flex align-center gap-2">
              <VIcon icon="bx-purchase-tag" color="success" size="20" />
              <div>
                <div class="text-body-2 font-weight-medium">
                  {{ cartStore.coupon_code }}
                </div>
              </div>
            </div>
            <VBtn icon size="x-small" variant="text" color="error" @click="removeCoupon">
              <VIcon icon="bx-x" size="18" />
            </VBtn>
          </div>
          <div v-else class="d-flex gap-2">
            <VTextField
              v-model="couponInput"
              density="compact"
              hide-details
              label="Kortingscode"
              :error="Boolean(couponError)"
              @keyup.enter="applyCoupon"
            />
            <VBtn
              color="primary"
              variant="tonal"
              :loading="applyingCoupon"
              :disabled="!couponInput.trim()"
              @click="applyCoupon"
            >
              Toepassen
            </VBtn>
          </div>
          <div v-if="couponError" class="text-caption text-error mt-1">
            {{ couponError }}
          </div>
        </div>

        <VDivider class="my-4" />

        <div class="d-flex justify-space-between">
          <span class="text-h6">Totaal</span>
          <h6 class="text-h6 text-primary">
            €{{ getTotalPrice.toFixed(2) }}
          </h6>
        </div>

        <!-- Participants Summary -->
        <VDivider class="mb-3" />

        <div class="d-flex flex-wrap align-center gap-2 mb-4">
          <VChip size="small" variant="tonal" color="secondary">
            <VIcon icon="bx-user" start size="16" />
            {{ uniqueParticipantCount }}x deelnemer{{ uniqueParticipantCount !== 1 ? 's' : '' }}
          </VChip>
        </div>

        <VDivider class="mb-4" />

        <!-- Invoice Details Section (only if enabled) -->
        <div v-if="invoiceEnabled" class="mb-4">
          <VCheckbox
            v-model="cartStore.invoice_required"
            label="Factuur gewenst voor bedrijf?"
            color="primary"
            hide-details="auto"
            class="mb-2"
          />

          <VExpandTransition>
            <div v-if="cartStore.invoice_required" class="mt-4">
              <VTextField
                v-model="cartStore.invoice_company_name"
                label="Bedrijfsnaam *"
                :rules="cartStore.invoice_required ? [v => !!v || 'Bedrijfsnaam is verplicht'] : []"
                variant="outlined"
                density="compact"
                class="mb-3"
              />
              <VTextField
                v-model="cartStore.invoice_vat_number"
                label="BTW-nummer"
                variant="outlined"
                density="compact"
                hint="Optioneel"
                persistent-hint
                class="mb-3"
              />
              <VTextarea
                v-model="cartStore.invoice_address"
                label="Factuuradres *"
                :rules="cartStore.invoice_required ? [v => !!v || 'Factuuradres is verplicht'] : []"
                variant="outlined"
                density="compact"
                rows="2"
                class="mb-3"
              />
              <VSelect
                v-model="cartStore.invoice_country"
                label="Land *"
                :items="countries"
                item-title="name"
                item-value="code"
                :rules="cartStore.invoice_required ? [v => !!v || 'Land is verplicht'] : []"
                variant="outlined"
                density="compact"
              />
            </div>
          </VExpandTransition>
        </div>

        <!-- Payment Methods (only if more than 1) -->
        <div v-if="paymentMethods.length > 1" class="mb-4">
          <div class="text-body-2 font-weight-medium mb-2">
            Betaalmethode
          </div>
          <VRow dense>
            <VCol v-for="method in paymentMethods" :key="method.value + method.title" cols="6">
              <VCard
                :variant="selectedPaymentMethod === method.value ? 'elevated' : 'outlined'"
                :color="selectedPaymentMethod === method.value ? method.color : undefined"
                class="cursor-pointer payment-method-card"
                @click="selectedPaymentMethod = method.value"
              >
                <VCardText class="py-2 px-3">
                  <div class="d-flex align-center gap-2">
                    <VIcon
                      :icon="method.value === 'stripe' ? 'bx-credit-card' : 'bx-money'"
                      :color="selectedPaymentMethod === method.value ? 'white' : method.color"
                      size="18"
                    />
                    <span
                      class="text-body-2"
                      :class="selectedPaymentMethod === method.value ? 'text-white' : ''"
                    >
                      {{ method.title }}
                    </span>
                  </div>
                </VCardText>
              </VCard>
            </VCol>
          </VRow>
        </div>

        <!-- Submit Error -->
        <VAlert
          v-if="submitError"
          type="error"
          variant="tonal"
          closable
          class="mb-4"
          @click:close="submitError = null"
        >
          {{ submitError }}
        </VAlert>

        <!-- Missing Requirements List (desktop only; mobile shows this in step 3) -->
        <div v-if="!canSubmit && missingRequirements.length > 0" class="missing-requirements mt-4 d-none d-md-block">
          <div class="d-flex align-center gap-2 mb-2">
            <VIcon icon="bx-info-circle" color="warning" size="18" />
            <span class="text-body-2 font-weight-medium text-warning">Nog te doen:</span>
          </div>
          <ul class="missing-list">
            <li v-for="(item, idx) in missingRequirements" :key="idx" class="text-body-2 text-medium-emphasis">
              {{ item }}
            </li>
          </ul>
        </div>

        <!-- Payment Button (desktop only) -->
        <VBtn
          block
          size="large"
          color="primary"
          class="mt-4 d-none d-md-flex"
          :loading="isSubmitting"
          :disabled="!canSubmit"
          @click="completePayment"
        >
          <VIcon icon="bx-lock-alt" start />
          Betaling bevestigen
        </VBtn>

        <!-- Cancellation Policy (tooltip) -->
        <div v-if="refundPolicyInfo" class="mt-4">
          <VTooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <p
                class="text-body-2 text-decoration-underline cursor-pointer text-center"
                v-bind="tooltipProps"
              >
                <VIcon :icon="refundPolicyInfo.icon" size="18" />
                Annulatievoorwaarden
              </p>
            </template>
            <div style=" padding: 12px; border-radius: 8px; color: #212121; max-inline-size: 280px;">
              <div class="d-flex align-center gap-2 mb-2">
                <VIcon :icon="refundPolicyInfo.icon" :color="refundPolicyInfo.color" size="18" />
                <span style="color: #212121; font-weight: 500;">{{ refundPolicyInfo.title }}</span>
              </div>
              <div style="color: #424242; font-size: 0.75rem; line-height: 1.4;">
                {{ refundPolicyInfo.description }}
              </div>
            </div>
          </VTooltip>
        </div>

        <div class="text-caption text-disabled mt-4 text-center">
          Door verder te gaan, accepteer je onze voorwaarden en beleid.
        </div>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss" scoped>
.payment-method-card {
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
}

.bg-success-subtle {
  background-color: rgba(var(--v-theme-success), 0.08);
}

.missing-requirements {
  border-radius: 0.5rem;
  background-color: rgba(var(--v-theme-warning), 0.08);
  padding-block: 0.75rem;
  padding-inline: 1rem;
}

.missing-list {
  margin: 0;
  padding-inline-start: 1.25rem;

  li {
    margin-block-end: 0.25rem;

    &:last-child {
      margin-block-end: 0;
    }
  }
}
</style>

<style lang="scss">
/* Global styles for VTooltip - must be unscoped */
.v-overlay.v-tooltip .v-overlay__content {
  background: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 15%) !important;
  color: #212121 !important;
}
</style>
